import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { formatarReajusteLinha } from '../components/TabelaReajustesFuncao';
import { ReajusteFuncaoLinha, SimuladorFolhaResultado } from '../simulador-folha.types';
import { extrairYyyyMm, formatarYyyyMmMesExtensoOuIndefinido } from '@/shared/utils/datas';

const styles = StyleSheet.create({
  page: { padding: 30, fontSize: 10 },
  title: { fontSize: 14, marginBottom: 12, fontWeight: 'bold' },
  sectionTitle: { fontSize: 11, marginTop: 12, marginBottom: 6, fontWeight: 'bold' },
  row: { flexDirection: 'row', borderBottom: '1px solid #ccc', paddingVertical: 4 },
  header: { fontWeight: 'bold', backgroundColor: '#f0f0f0' },
  footer: { fontWeight: 'bold', backgroundColor: '#e9ecef' },
  cell: { flex: 1, paddingHorizontal: 4 },
  cellWide: { flex: 2, paddingHorizontal: 4 },
  cellRight: { flex: 1, paddingHorizontal: 4, textAlign: 'right' },
});

function formatMoney(value: number) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

interface Props {
  resultado: SimuladorFolhaResultado;
  competenciaLabel: string;
  reajustes: ReajusteFuncaoLinha[];
}

export function SimuladorFolhaPDF({ resultado, competenciaLabel, reajustes }: Props) {
  const { resumo, porFuncao, porLocal, porMes, detalhes } = resultado;
  const totalSalarioVigente = reajustes.reduce((acc, item) => acc + (item.ultimoSalarioVigente ?? 0), 0);
  const funcoesComSalario = reajustes.filter((item) => item.ultimoSalarioVigente != null).length;
  const totaisMes = (porMes ?? []).reduce(
    (acc, item) => ({
      totalAtual: acc.totalAtual + item.totalAtual,
      totalProjetado: acc.totalProjetado + item.totalProjetado,
      diferenca: acc.diferenca + item.diferenca,
    }),
    { totalAtual: 0, totalProjetado: 0, diferenca: 0 },
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>Simulador de Folha de Pagamento</Text>
        <Text>Competência: {competenciaLabel}</Text>

        <Text style={styles.sectionTitle}>Reajustes por função</Text>
        <View style={[styles.row, styles.header]}>
          <Text style={styles.cellWide}>Função</Text>
          <Text style={styles.cell}>Reajuste</Text>
        </View>
        {reajustes.map((item) => (
          <View key={item.funcaoId} style={styles.row}>
            <Text style={styles.cellWide}>{item.funcaoNome}</Text>
            <Text style={styles.cell}>{formatarReajusteLinha(item)}</Text>
          </View>
        ))}
        <View style={[styles.row, styles.footer]}>
          <Text style={styles.cellWide}>Total ({funcoesComSalario} funções com salário)</Text>
          <Text style={styles.cell}>{funcoesComSalario > 0 ? formatMoney(totalSalarioVigente) : '—'}</Text>
        </View>

        <Text style={styles.sectionTitle}>Resumo</Text>
        <View style={[styles.row, styles.header]}>
          <Text style={styles.cell}>Folha atual</Text>
          <Text style={styles.cell}>Folha projetada</Text>
          <Text style={styles.cell}>Diferença</Text>
          <Text style={styles.cell}>Servidores</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.cell}>{formatMoney(resumo.totalAtual)}</Text>
          <Text style={styles.cell}>{formatMoney(resumo.totalProjetado)}</Text>
          <Text style={styles.cell}>{formatMoney(resumo.diferenca)}</Text>
          <Text style={styles.cell}>{resumo.quantidadeServidores}</Text>
        </View>

        <Text style={styles.sectionTitle}>Por função</Text>
        <View style={[styles.row, styles.header]}>
          <Text style={styles.cellWide}>Função</Text>
          <Text style={styles.cell}>Qtd.</Text>
          <Text style={styles.cellRight}>Atual</Text>
          <Text style={styles.cellRight}>Projetado</Text>
          <Text style={styles.cellRight}>Diferença</Text>
        </View>
        {porFuncao.map((item) => (
          <View key={item.funcaoId} style={styles.row}>
            <Text style={styles.cellWide}>{item.funcaoNome}</Text>
            <Text style={styles.cell}>{item.quantidade}</Text>
            <Text style={styles.cellRight}>{formatMoney(item.totalAtual)}</Text>
            <Text style={styles.cellRight}>{formatMoney(item.totalProjetado)}</Text>
            <Text style={styles.cellRight}>{formatMoney(item.diferenca)}</Text>
          </View>
        ))}
        <View style={[styles.row, styles.footer]}>
          <Text style={styles.cellWide}>Total</Text>
          <Text style={styles.cell}>{resumo.quantidadeServidores}</Text>
          <Text style={styles.cellRight}>{formatMoney(resumo.totalAtual)}</Text>
          <Text style={styles.cellRight}>{formatMoney(resumo.totalProjetado)}</Text>
          <Text style={styles.cellRight}>{formatMoney(resumo.diferenca)}</Text>
        </View>

        <Text style={styles.sectionTitle}>Por local de lotação</Text>
        <View style={[styles.row, styles.header]}>
          <Text style={styles.cellWide}>Local</Text>
          <Text style={styles.cell}>Qtd.</Text>
          <Text style={styles.cellRight}>Atual</Text>
          <Text style={styles.cellRight}>Projetado</Text>
          <Text style={styles.cellRight}>Diferença</Text>
        </View>
        {porLocal.map((item) => (
          <View key={item.localNome} style={styles.row}>
            <Text style={styles.cellWide}>{item.localNome}</Text>
            <Text style={styles.cell}>{item.quantidade}</Text>
            <Text style={styles.cellRight}>{formatMoney(item.totalAtual)}</Text>
            <Text style={styles.cellRight}>{formatMoney(item.totalProjetado)}</Text>
            <Text style={styles.cellRight}>{formatMoney(item.diferenca)}</Text>
          </View>
        ))}
        <View style={[styles.row, styles.footer]}>
          <Text style={styles.cellWide}>Total</Text>
          <Text style={styles.cell}>{resumo.quantidadeServidores}</Text>
          <Text style={styles.cellRight}>{formatMoney(resumo.totalAtual)}</Text>
          <Text style={styles.cellRight}>{formatMoney(resumo.totalProjetado)}</Text>
          <Text style={styles.cellRight}>{formatMoney(resumo.diferenca)}</Text>
        </View>

        <Text style={styles.sectionTitle}>Projeção mensal (ano inteiro)</Text>
        <View style={[styles.row, styles.header]}>
          <Text style={styles.cellWide}>Mês</Text>
          <Text style={styles.cell}>Qtd.</Text>
          <Text style={styles.cellRight}>Atual</Text>
          <Text style={styles.cellRight}>Projetado</Text>
          <Text style={styles.cellRight}>Diferença</Text>
        </View>
        {(porMes ?? []).map((item) => (
          <View key={item.competencia} style={styles.row}>
            <Text style={styles.cellWide}>
              {formatarYyyyMmMesExtensoOuIndefinido(extrairYyyyMm(item.competencia))}
            </Text>
            <Text style={styles.cell}>{item.quantidadeServidores}</Text>
            <Text style={styles.cellRight}>{formatMoney(item.totalAtual)}</Text>
            <Text style={styles.cellRight}>{formatMoney(item.totalProjetado)}</Text>
            <Text style={styles.cellRight}>{formatMoney(item.diferenca)}</Text>
          </View>
        ))}
        <View style={[styles.row, styles.footer]}>
          <Text style={styles.cellWide}>Total acumulado</Text>
          <Text style={styles.cell} />
          <Text style={styles.cellRight}>{formatMoney(totaisMes.totalAtual)}</Text>
          <Text style={styles.cellRight}>{formatMoney(totaisMes.totalProjetado)}</Text>
          <Text style={styles.cellRight}>{formatMoney(totaisMes.diferenca)}</Text>
        </View>
      </Page>

      <Page size="A4" style={styles.page} orientation="landscape">
        <Text style={styles.sectionTitle}>Detalhamento por servidor</Text>
        <View style={[styles.row, styles.header]}>
          <Text style={styles.cellWide}>Nome</Text>
          <Text style={styles.cell}>Tipo</Text>
          <Text style={styles.cell}>Função</Text>
          <Text style={styles.cell}>Local</Text>
          <Text style={styles.cellRight}>Atual</Text>
          <Text style={styles.cellRight}>Projetado</Text>
          <Text style={styles.cellRight}>Diferença</Text>
        </View>
        {detalhes.map((item) => (
          <View key={`${item.tipo}-${item.pessoaId}`} style={styles.row}>
            <Text style={styles.cellWide}>{item.nome}</Text>
            <Text style={styles.cell}>{item.tipo === 'defensor' ? 'Defensor' : 'Colaborador'}</Text>
            <Text style={styles.cell}>{item.funcaoNome}</Text>
            <Text style={styles.cell}>{item.localNome}</Text>
            <Text style={styles.cellRight}>{formatMoney(item.valorAtual)}</Text>
            <Text style={styles.cellRight}>{formatMoney(item.valorProjetado)}</Text>
            <Text style={styles.cellRight}>{formatMoney(item.diferenca)}</Text>
          </View>
        ))}
        <View style={[styles.row, styles.footer]}>
          <Text style={styles.cellWide}>Total ({resumo.quantidadeServidores} servidores)</Text>
          <Text style={styles.cell} />
          <Text style={styles.cell} />
          <Text style={styles.cell} />
          <Text style={styles.cellRight}>{formatMoney(resumo.totalAtual)}</Text>
          <Text style={styles.cellRight}>{formatMoney(resumo.totalProjetado)}</Text>
          <Text style={styles.cellRight}>{formatMoney(resumo.diferenca)}</Text>
        </View>
      </Page>
    </Document>
  );
}
